import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useChat } from 'chat/useChat';
import { Skeleton } from 'components/Skeleton';
import { push } from 'connected-react-router';
import app from 'new-ui/app';
import { useTranslation } from 'utils/modifiedTranslation';
import { NBA_CHAT_ROOM } from 'chat/constants';
import { GA_NBA_CATEGORY, GA_NBA_EVENTS } from 'modules/ga/generalEvents';
import { useMatchesFilter } from 'modules/matchesFilter/MatchesFilterContext';
import { getUserWixId, getUserCondition } from 'modules/user/utils';
import { useAsyncAction } from 'utils/hooks/useAsyncAction';
import {
  SUCCESS, PENDING, PRISTINE,
} from 'utils/constants/queryStatuses';
import StepJourneyCard from './StepJourenyCard/StepJourenyCard';
import styles from './Nba.module.css';
import { startNbaSession, updateNbaState } from './api';
import {
  calculateEvaluationContext,
  createFrontendActionHandlers,
  getCurrentAction,
  getFilteredMilestones,
  getMilestonesStatus,
  getDynamicActionComponentConfig,
  resolveActiveMilestoneAndStep,
  executeAction,
} from './nba.utils';
import { ACTION_IDENTIFIER_RENDER_COMPONENT, DISPLAY_CONTEXTS } from './nba.constants';

const Nba = ({ displayContext }) => {
  const { t } = useTranslation();

  const { data: profile } = useSelector((state) => state.profile);
  const { trials, treatments } = useSelector((state) => state.matches);
  const dispatch = useDispatch();

  const [sessionError, setSessionError] = useState(null);
  const [sessionData, setSessionData] = useState(null);
  const [activeMilestoneId, setActiveMilestoneId] = useState(null);
  const [selectedStepId, setSelectedStepId] = useState(null);
  const [currentActionName, setCurrentActionName] = useState(null);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [dynamicActionComponent, setDynamicActionComponent] = useState(null);
  const { treatmentTypes: selectedTreatmentTypes, setInterventionalTypes, setTreatmentTypes } = useMatchesFilter();
  const { openChatPopup } = useChat(NBA_CHAT_ROOM);

  const userWixId = getUserWixId(profile);
  const condition = getUserCondition(profile);

  const {
    data: sessionResponse,
    status: sessionStatus,
  } = useAsyncAction({
    action: startNbaSession,
    parameters: { userWixId, cancerType: condition },
    isActive: !!userWixId && !!condition,
    dataId: 'nbaSession',
  });

  const isLoadingSession = sessionStatus === PENDING || sessionStatus === PRISTINE;

  const evaluationContext = useMemo(
    () => calculateEvaluationContext({
      profile, trials, treatments, selectedTreatmentTypes,
    }),
    [profile, trials, treatments, selectedTreatmentTypes],
  );

  const frontendActionHandlers = useMemo(
    () => createFrontendActionHandlers({
      setDynamicActionComponentRef: setDynamicActionComponent,
      setInterventionalTypes,
      setTreatmentTypes,
      openChatPopup,
      pushRoute: (path) => dispatch(push(path)),
    }),
    [setInterventionalTypes, setTreatmentTypes, openChatPopup, dispatch],
  );

  const filteredMilestones = useMemo(() => {
    const milestones = sessionData?.nbaGuide?.milestones;
    return getFilteredMilestones(milestones, evaluationContext, displayContext);
  }, [sessionData, evaluationContext, displayContext]);

  const { milestonesForCard, completedMilestoneIds } = useMemo(
    () => getMilestonesStatus(filteredMilestones, activeMilestoneId),
    [filteredMilestones, activeMilestoneId],
  );

  const currentAction = useMemo(
    () => getCurrentAction(
      sessionData,
      activeMilestoneId,
      selectedStepId,
      currentActionName,
      evaluationContext,
      displayContext,
    ),
    [sessionData, activeMilestoneId, selectedStepId, currentActionName, evaluationContext, displayContext],
  );

  useEffect(() => {
    setDynamicActionComponent(getDynamicActionComponentConfig({
      currentAction,
      userWixId,
      sessionData,
      activeMilestoneId,
      selectedStepId,
      setInterventionalTypes,
      setTreatmentTypes,
      setDynamicActionComponentCallback: setDynamicActionComponent,
      openChatPopup,
    }));
  }, [
    userWixId,
    currentAction,
    sessionData,
    activeMilestoneId,
    selectedStepId,
    setInterventionalTypes,
    setTreatmentTypes,
    openChatPopup,
  ]);

  const persistNbaState = useCallback(async (newState) => {
    if (!userWixId) {
      return;
    }

    const payload = {
      userWixId,
      currentMilestone: newState.milestoneId,
      currentStep: newState.stepId,
      currentAction: newState.actionName,
    };

    try {
      await updateNbaState(payload);
    } catch (error) {
      console.error('Failed to persist NBA state:', error);
    }
  }, [userWixId]);

  useEffect(() => {
    const {
      nextActiveMilestoneId,
      nextSelectedStepId,
      shouldPersist,
      persistPayload,
    } = resolveActiveMilestoneAndStep({
      milestonesForCard,
      activeMilestoneId,
      selectedStepId,
    });

    const hasNothingChanged = nextActiveMilestoneId === activeMilestoneId && nextSelectedStepId === selectedStepId;
    if (hasNothingChanged) {
      return;
    }

    setActiveMilestoneId(nextActiveMilestoneId);
    setSelectedStepId(nextSelectedStepId);
    setCurrentActionName(null);

    if (shouldPersist && persistPayload) {
      persistNbaState(persistPayload);
    }
  }, [milestonesForCard, activeMilestoneId, selectedStepId, persistNbaState]);

  useEffect(() => {
    if (sessionStatus === PENDING || sessionStatus === PRISTINE) {
      setSessionError(null);
      return;
    }

    const errorMessage = sessionResponse?.error || sessionResponse?.message || 'Failed to start NBA session (API response missing expected data).';

    if (!(sessionStatus === SUCCESS && sessionResponse)) {
      setSessionError(errorMessage);
      return;
    }

    if (!(sessionResponse.nbaGuide && sessionResponse.currentMilestone && sessionResponse.currentStep)) {
      setSessionError(errorMessage);
      return;
    }

    setSessionData(sessionResponse);
    setActiveMilestoneId(sessionResponse.currentMilestone);
    setSelectedStepId(sessionResponse.currentStep);
    setCurrentActionName(sessionResponse.currentAction || null);
  }, [sessionResponse, sessionStatus]);

  const handlePagedActionChange = useCallback(async (newActionName) => {
    if (newActionName !== currentActionName) {
      setCurrentActionName(newActionName);
      await persistNbaState({
        milestoneId: activeMilestoneId,
        stepId: selectedStepId,
        actionName: newActionName,
      });
    }
  }, [currentActionName, activeMilestoneId, selectedStepId, persistNbaState]);

  const handleMilestoneClick = useCallback(async (milestoneName) => {
    if (milestoneName === activeMilestoneId || !sessionData?.nbaGuide?.milestones) {
      return;
    }

    app.sendGoogleAnalyticsEvent(
      GA_NBA_CATEGORY,
      GA_NBA_EVENTS.MILESTONE_TAB_CLICKED,
      { milestone: milestoneName },
    );

    const newMilestone = milestonesForCard.find((m) => m?.name === milestoneName);
    if (!newMilestone) {
      return;
    }

    const firstStep = newMilestone.steps?.[0];
    const firstStepName = firstStep?.name;

    setActiveMilestoneId(milestoneName);
    setSelectedStepId(firstStepName || null);
    setCurrentActionName(null);
  }, [activeMilestoneId, sessionData, milestonesForCard]);

  const handleStepClick = useCallback(async (stepName) => {
    app.sendGoogleAnalyticsEvent(
      GA_NBA_CATEGORY,
      GA_NBA_EVENTS.STEP_TAB_CLICKED,
      { milestone: activeMilestoneId, step: stepName },
    );
    if (stepName === selectedStepId || !sessionData?.nbaGuide?.milestones || !activeMilestoneId) {
      return;
    }

    setSelectedStepId(stepName);
    setCurrentActionName(null);
  }, [selectedStepId, activeMilestoneId, sessionData]);

  const handleActionClick = useCallback(async (actionToExecute) => {
    if (!actionToExecute || typeof actionToExecute?.name !== 'string') {
      console.error('Invalid action clicked:', actionToExecute);
      return;
    }

    if (actionToExecute.name !== currentActionName) {
      setCurrentActionName(actionToExecute.name);
      await persistNbaState({
        milestoneId: activeMilestoneId,
        stepId: selectedStepId,
        actionName: actionToExecute.name,
      });
    }

    const identifier = actionToExecute.onActionCallIdentifier;
    if (!identifier || identifier === ACTION_IDENTIFIER_RENDER_COMPONENT) {
      return;
    }

    try {
      setIsActionLoading(true);

      const context = {
        userWixId,
        sessionData,
        activeMilestoneId,
        selectedStepId,
        actionName: actionToExecute.name,
        action: actionToExecute,
      };

      await executeAction({
        actionToExecute,
        frontendActionHandlers,
        context,
      });
    } catch (frontendError) {
      console.error(`Error executing frontend handler for action "${actionToExecute.name}" (identifier: ${identifier}):`, frontendError);
    } finally {
      setIsActionLoading(false);
    }
  }, [
    sessionData,
    activeMilestoneId,
    selectedStepId,
    currentActionName,
    frontendActionHandlers,
    persistNbaState,
    userWixId,
  ]);

  if (isLoadingSession) {
    return (
      <div className={styles.main_box}>
        <Skeleton width="100%" className={styles.skeleton} />
      </div>
    );
  }

  if (sessionError) {
    return null;
  }

  const canRenderCards = !!(sessionData?.nbaGuide && activeMilestoneId && selectedStepId);

  return (
    <div className={styles.main_box}>
      {canRenderCards ? (
        <StepJourneyCard
          milestones={milestonesForCard}
          activeMilestoneId={activeMilestoneId}
          selectedStepId={selectedStepId}
          completedMilestoneIds={completedMilestoneIds}
          onMilestoneClick={handleMilestoneClick}
          onStepClick={handleStepClick}
          onActionClick={handleActionClick}
          isActionLoading={isActionLoading}
          dynamicActionComponent={dynamicActionComponent}
          onPagedActionChange={handlePagedActionChange}
          evaluationContext={evaluationContext}
          displayContext={displayContext}
        />
      ) : (
        !isLoadingSession && <div>{t('nba.guidance_unavailable', 'Guidance data is currently unavailable.')}</div>
      )}
    </div>
  );
};

Nba.propTypes = {
  displayContext: PropTypes.oneOf(DISPLAY_CONTEXTS),
};

export default Nba;
